import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { useDroppable } from '@dnd-kit/core';
import { alpha } from '@mui/material/styles';
import {
  BOARD_LETTERS,
  BOARD_SIZE,
  getModifierMeta,
  getTileId,
} from './boardConfig';
import LetterTile from './LetterTile';
import { LETTER_POINTS } from './config/gameConfig';

const TILE_TRANSITION =
  'filter 180ms ease, box-shadow 180ms ease, border-color 180ms ease, transform 100ms ease-out';

function ScrabbleSquare({
  tileId,
  coord,
  modifierLabel,
  squareModifierDetails,
  background,
  committedTile,
  stagedTile,
  validationState,
  isHoveredDropTarget,
  onInspectTile,
  placementMode,
  onSquareClick,
  isRecentlyPlaced,
  isCenter,
}) {
  const theme = useTheme();
  const hasTile = Boolean(committedTile || stagedTile);
  const { setNodeRef, isOver } = useDroppable({
    id: `square:${tileId}`,
    data: {
      type: 'square',
      tileId,
    },
  });

  const isPlaceable = placementMode && !hasTile;

  const stateBorderColor =
    validationState === 'valid'
      ? theme.palette.success.main
      : validationState === 'invalid'
        ? theme.palette.error.main
        : validationState === 'pending'
          ? theme.palette.warning.main
          : theme.palette.divider;

  const inspectTile = React.useMemo(() => {
    const tile = committedTile || stagedTile || null;
    return {
      ...(tile || {}),
      boardSquareModifiers: squareModifierDetails,
      boardSquareLabel: modifierLabel,
      boardSquareCoord: coord,
    };
  }, [committedTile, stagedTile, squareModifierDetails, modifierLabel, coord]);

  return (
    <Box
      ref={setNodeRef}
      role={isPlaceable ? 'button' : hasTile ? 'button' : 'presentation'}
      tabIndex={isPlaceable || hasTile ? 0 : -1}
      aria-label={`Square ${coord}${modifierLabel ? ` ${modifierLabel}` : ''}`}
      onClick={isPlaceable ? () => onSquareClick?.(tileId) : undefined}
      onMouseEnter={() => onInspectTile?.(inspectTile)}
      onMouseLeave={() => onInspectTile?.(null)}
      onPointerDown={() => onInspectTile?.(inspectTile)}
      sx={{
        aspectRatio: '1 / 1',
        minHeight: { xs: 22, sm: 26, md: 34 },
        borderRadius: 0.5,
        border: `2px solid ${
          isHoveredDropTarget || isOver
            ? theme.palette.primary.main
            : isPlaceable
              ? alpha(theme.palette.success.main, 0.75)
              : placementMode && hasTile
                ? alpha(theme.palette.error.main, 0.4)
                : stateBorderColor
        }`,
        backgroundColor: background,
        display: 'grid',
        placeItems: 'center',
        cursor: isPlaceable
          ? 'crosshair'
          : placementMode && hasTile
            ? 'not-allowed'
            : hasTile
              ? 'pointer'
              : 'default',
        userSelect: 'none',
        transition: TILE_TRANSITION,
        position: 'relative',
        opacity: placementMode && hasTile ? 0.4 : 1,
        zIndex: isRecentlyPlaced ? 10 : 'auto',
        ...(isCenter
          ? {
              outline: `2px solid ${alpha(theme.palette.warning.main, 0.65)}`,
              outlineOffset: '-3px',
            }
          : {}),
        boxShadow: isHoveredDropTarget
          ? `inset 0 0 0 999px ${alpha(theme.palette.primary.main, 0.12)}, 0 0 0 2px ${alpha(
              theme.palette.primary.main,
              0.55
            )}`
          : isPlaceable
            ? `inset 0 0 0 999px ${alpha(theme.palette.success.main, 0.10)}`
            : placementMode && hasTile
              ? `inset 0 0 0 999px ${alpha(theme.palette.error.main, 0.08)}`
              : undefined,
        ...(isRecentlyPlaced
          ? {
              '@keyframes modifierLand': {
                '0%': {
                  transform: 'scale(0.82)',
                  boxShadow: `0 0 0 0 ${alpha(theme.palette.success.main, 0)}`,
                },
                '38%': {
                  transform: 'scale(1.22)',
                  boxShadow: `0 0 0 8px ${alpha(theme.palette.success.main, 0.38)}`,
                },
                '70%': {
                  transform: 'scale(0.96)',
                  boxShadow: `0 0 0 4px ${alpha(theme.palette.success.main, 0.16)}`,
                },
                '100%': {
                  transform: 'scale(1)',
                  boxShadow: `0 0 0 0 ${alpha(theme.palette.success.main, 0)}`,
                },
              },
              animation: 'modifierLand 620ms cubic-bezier(0.22, 1, 0.36, 1) forwards',
            }
          : {}),
        ...(isPlaceable
          ? {
              '&:hover, &:focus-visible': {
                transform: 'scale(1.1)',
                zIndex: 10,
                boxShadow: `inset 0 0 0 999px ${alpha(theme.palette.success.main, 0.22)}, 0 0 0 2px ${alpha(theme.palette.success.main, 0.75)}`,
                outline: 'none',
              },
            }
          : !placementMode && hasTile
            ? {
                '&:hover, &:focus-visible': {
                  transform: 'scale(1.14)',
                  zIndex: 10,
                  boxShadow: `0 4px 14px ${alpha(theme.palette.scrabble.tileText, 0.32)}`,
                  outline: 'none',
                },
              }
            : {}),
      }}
    >
      {committedTile ? (
        <LetterTile
          tile={{ ...committedTile, points: LETTER_POINTS[committedTile.letter] || 0 }}
          draggable={false}
          compact
          variant="board"
          boardSquareModifierCount={squareModifierDetails.length}
        />
      ) : stagedTile ? (
        <LetterTile
          tile={{ ...stagedTile, points: LETTER_POINTS[stagedTile.letter] || 0 }}
          dragData={{
            source: 'board',
            tile: stagedTile,
            fromTileId: tileId,
          }}
          compact
          variant="board"
          boardSquareModifierCount={squareModifierDetails.length}
        />
      ) : isCenter ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1, userSelect: 'none' }}>
          <Typography sx={{ fontSize: { xs: 10, sm: 13 }, lineHeight: 1, color: theme.palette.warning.main, opacity: 0.85 }}>
            ★
          </Typography>
          <Typography sx={{ fontSize: { xs: 6, sm: 7.5 }, fontWeight: 700, opacity: 0.55, letterSpacing: 0.1 }}>
            {modifierLabel}
          </Typography>
        </Box>
      ) : (
        <Typography
          sx={{
            fontSize: { xs: 8, sm: 9.5 },
            fontWeight: 700,
            opacity: 0.58,
            letterSpacing: 0.2,
            userSelect: 'none',
          }}
        >
          {modifierLabel}
        </Typography>
      )}
    </Box>
  );
}

const MemoScrabbleSquare = React.memo(
  ScrabbleSquare,
  (prev, next) =>
    prev.tileId === next.tileId &&
    prev.coord === next.coord &&
    prev.modifierLabel === next.modifierLabel &&
    prev.squareModifierDetails === next.squareModifierDetails &&
    prev.background === next.background &&
    prev.committedTile === next.committedTile &&
    prev.stagedTile === next.stagedTile &&
    prev.validationState === next.validationState &&
    prev.isHoveredDropTarget === next.isHoveredDropTarget &&
    prev.onInspectTile === next.onInspectTile &&
    prev.placementMode === next.placementMode &&
    prev.onSquareClick === next.onSquareClick &&
    prev.isRecentlyPlaced === next.isRecentlyPlaced &&
    prev.isCenter === next.isCenter
);

function ScrabbleBoard({
  boardTiles,
  committedBoard,
  stagedBoard,
  tileValidationState,
  hoveredSquareId,
  onInspectTile,
  placementMode,
  onSquareClick,
  recentlyPlacedSquareId,
}) {
  const theme = useTheme();

  const squareUiMetaByTileId = React.useMemo(() => {
    const squareMeta = {};

    for (let row = 0; row < BOARD_SIZE; row += 1) {
      for (let col = 0; col < BOARD_SIZE; col += 1) {
        const tileId = getTileId(row, col);
        const coord = `${BOARD_LETTERS[col]}${row + 1}`;
        const squareModifiers = boardTiles[tileId]?.modifiers || ['NORMAL'];
        const modifierMetaList = squareModifiers.map(getModifierMeta);
        const colorMeta =
          modifierMetaList.find((modifierMeta) => modifierMeta.colorKey !== 'normal') ||
          modifierMetaList[0];

        squareMeta[tileId] = {
          coord,
          background: theme.palette.scrabble[colorMeta.colorKey],
          modifierLabel: modifierMetaList
            .map((modifierMeta) => modifierMeta.label)
            .filter(Boolean)
            .join('+'),
          squareModifierDetails: modifierMetaList
            .filter((modifierMeta) => String(modifierMeta.label || '').trim())
            .map((modifierMeta, index) => ({
              id: `${tileId}-${modifierMeta.label}-${index}`,
              label: modifierMeta.label,
              description: modifierMeta.description,
            })),
        };
      }
    }

    return squareMeta;
  }, [boardTiles, theme.palette.scrabble]);

  const cells = [];
  for (let row = -1; row <= BOARD_SIZE; row += 1) {
    for (let col = -1; col <= BOARD_SIZE; col += 1) {
      const isTopOrBottom = row === -1 || row === BOARD_SIZE;
      const isLeftOrRight = col === -1 || col === BOARD_SIZE;
      const isCorner = isTopOrBottom && isLeftOrRight;

      if (isCorner) {
        cells.push(<Box key={`corner-${row}-${col}`} />);
        continue;
      }

      if (isTopOrBottom && col >= 0 && col < BOARD_SIZE) {
        cells.push(
          <Typography
            key={`col-${row}-${col}`}
            variant="caption"
            sx={{
              textAlign: 'center',
              color: theme.palette.scrabble.coord,
              fontWeight: 600,
            }}
          >
            {BOARD_LETTERS[col]}
          </Typography>
        );
        continue;
      }

      if (isLeftOrRight && row >= 0 && row < BOARD_SIZE) {
        cells.push(
          <Typography
            key={`row-${row}-${col}`}
            variant="caption"
            sx={{
              textAlign: 'center',
              color: theme.palette.scrabble.coord,
              fontWeight: 600,
            }}
          >
            {row + 1}
          </Typography>
        );
        continue;
      }

      const tileId = getTileId(row, col);
      const squareMeta = squareUiMetaByTileId[tileId];

      cells.push(
        <MemoScrabbleSquare
          key={tileId}
          tileId={tileId}
          coord={squareMeta.coord}
          modifierLabel={squareMeta.modifierLabel}
          squareModifierDetails={squareMeta.squareModifierDetails}
          background={squareMeta.background}
          committedTile={committedBoard[tileId]}
          stagedTile={stagedBoard[tileId]}
          validationState={tileValidationState[tileId]}
          isHoveredDropTarget={hoveredSquareId === tileId}
          onInspectTile={onInspectTile}
          placementMode={placementMode}
          onSquareClick={onSquareClick}
          isRecentlyPlaced={recentlyPlacedSquareId === tileId}
          isCenter={row === 7 && col === 7}
        />
      );
    }
  }

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '100%',
        margin: '0 auto',
        padding: { xs: 0.3, sm: 0.6 },
        borderRadius: 2,
        backgroundColor: theme.palette.scrabble.rack,
        border: `1px solid ${alpha(theme.palette.scrabble.tileText, 0.22)}`,
        boxShadow: `inset 0 1px 0 ${alpha('#fff', 0.24)}`,
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '12px repeat(15, minmax(16px, 1fr)) 12px',
          gap: { xs: 0, sm: 0.08 },
          backgroundColor: theme.palette.divider,
          padding: { xs: 0, sm: 0.08 },
          borderRadius: 0.8,
        }}
      >
        {cells}
      </Box>
    </Box>
  );
}

export default ScrabbleBoard;